import { Fragment, memo } from 'react';
import { useTranslation } from 'react-i18next';

import { SVGIconType } from '../../../../constants/svgIcon';
import { RewardReferralRole } from '../../../../models/RewardStore';
import { Profile } from '../../../../models/UserStore';
import {
  AvatarNode,
  LoadMoreSpinner,
  ScrollArrows,
  SvgIcon,
  UserCell,
  UserCellProps,
  WebStoreProductCell,
  WebStoreProductCellProps,
} from '../../../Molecules';

import styles from './WebStoreReferralRewardTier1Template.module.css';

export type WebStoreReferralRewardTier1TemplateProps = {
  profile: Profile | undefined | null;
  rewardReferralRole: RewardReferralRole | undefined;
  purchasedProduct: WebStoreProductCellProps[];
  data: UserCellProps[];
  hasShowMore: boolean;
  hasCursor: boolean;
  onClickShowMore: () => void;
  loadMoreItems: () => void;
};

export const WebStoreReferralRewardTier1Template = memo(
  ({
    profile,
    rewardReferralRole,
    purchasedProduct,
    data,
    hasShowMore,
    hasCursor,
    onClickShowMore,
    loadMoreItems,
  }: WebStoreReferralRewardTier1TemplateProps) => {
    const { t } = useTranslation();
    return (
      <div className={styles.webStoreReferralReward}>
        {data.length > 10 && <ScrollArrows />}
        <div className={styles.centeredContainer}>
          {profile ? (
            <div className={styles.container}>
              <AvatarNode profile={profile} size={80} />
              <div className={styles.column}>
                <div className={styles.userName}>{profile?.name}</div>
                <div className={styles.userDescription}>
                  <span>
                    {profile.statistics.joyajoyCustomerReferralCount ?? 0}
                  </span>
                  {t('Referred Users')}
                </div>
              </div>
            </div>
          ) : null}
          <div className={styles.paleBanner}>
            <div className={styles.rowBannerText}>
              <SvgIcon name={SVGIconType.Referral} width={60} height={60} />
              <div className={styles.centerText}>
                {t(
                  rewardReferralRole === RewardReferralRole.Partner
                    ? 'When your referred JOYAJOY Distributors archieve sales performance, you can receive 10% of their total net sales as a management reward.'
                    : 'When new users that you and your referred users purchased any products, you will receive rewards!',
                )}
              </div>
            </div>
          </div>

          {purchasedProduct.length > 0 && (
            <>
              <div className={styles.listTitle}>{t('Purchased Products')}</div>
              <div className={styles.listView}>
                {purchasedProduct.map((item, index) => (
                  <Fragment key={index}>
                    {index != 0 && <div className={styles.cellSpace} />}
                    <WebStoreProductCell key={index} {...item} />
                  </Fragment>
                ))}
              </div>
              {hasShowMore && (
                <div className={styles.showMoreButtonContainer}>
                  <SvgIcon
                    name={SVGIconType.Arrow}
                    className={styles.showMoreArrowIcon}
                  />
                  <div
                    className={styles.showMoreButton}
                    onClick={onClickShowMore}
                  >
                    {t('Show More')}
                  </div>
                </div>
              )}
            </>
          )}

          <div className={styles.listTitle}>{t('Referred Users (Tier 2)')}</div>
          <div className={styles.listView}>
            {data.length === 0 ? (
              <div className={styles.emptyContainer}>
                <SvgIcon
                  name={SVGIconType.DoublePerson}
                  className={styles.emptyReferralIcon}
                />
                <div className={styles.emptyReferralText}>
                  {t('No Users Referred')}
                </div>
              </div>
            ) : (
              data.map((item, index) => (
                <Fragment key={index}>
                  {index != 0 && <div className={styles.cellSpace} />}
                  <UserCell key={index} {...item} />
                </Fragment>
              ))
            )}
          </div>
          {hasCursor && (
            <LoadMoreSpinner
              hasCursor={hasCursor}
              loadMoreItems={loadMoreItems}
            />
          )}
        </div>
      </div>
    );
  },
);
